import React, { useEffect, useState } from "react";
import CommonDialog from "app/components/common/CommonDialog";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { updateRazorPayConfig } from "app/services/school/paymetGateway/razorpay";
import useSelectorHook from "app/services/redux/useSelectorHook";
import { toast } from "react-toastify";
import { useMutation } from "react-query";

const RazorPayConfigDialog = ({
  open,
  onClose,
  schoolDocId,
  razorPayKey,
  razorPaySecret,
  active,
  refetch=() => {},
}) => {
  const { userDetail } = useSelectorHook();
  const userDocId = userDetail?.staffDetail?._id;

  const [formData, setFormData] = useState({
    razorPay_key_id: "",
    razorPay_key_secret: "",
    active: true,
  });

  const [showKeyId, setShowKeyId] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  // Initialize form data from props when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        razorPay_key_id: razorPayKey || "",
        razorPay_key_secret: razorPaySecret || "",
        active: active ?? true,
      });
      setShowKeyId(false);
      setShowSecret(false);
    } else {
      // Reset form when dialog closes
      setFormData({
        razorPay_key_id: "",
        razorPay_key_secret: "",
        active: true,
      });
      setShowKeyId(false);
      setShowSecret(false);
    }
  }, [open, razorPayKey, razorPaySecret, active]);

  // Update mutation
  const { mutate: updateMutate, isLoading: saveLoading } = useMutation(
    (payload) => updateRazorPayConfig(payload),
    {
      onSuccess: (response) => {
        toast.success(
          response?.data?.message ||
            "Razorpay configuration updated successfully"
        );
        handleClose();
        refetch();
      },
      onError: (error) => {
        console.error("Error updating Razorpay config:", error);
        toast.error(
          error?.response?.data?.message ||
            "Failed to update Razorpay configuration"
        );
      },
    }
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      active: e.target.checked,
    }));
  };

  const handleSave = () => {
    // Validation
    if (!formData.razorPay_key_id.trim()) {
      toast.error("Razorpay Key ID is required");
      return;
    }

    if (!formData.razorPay_key_secret.trim()) {
      toast.error("Razorpay Key Secret is required");
      return;
    }

    const payload = {
      schoolDocId,
      razorPay_key_id: formData.razorPay_key_id,
      razorPay_key_secret: formData.razorPay_key_secret,
      active: formData.active,
    };
    console.log("Payload",payload);
    

    updateMutate(payload);
  };

  const handleClose = () => {
    setFormData({
      razorPay_key_id: "",
      razorPay_key_secret: "",
      active: true,
    });
    setShowKeyId(false);
    setShowSecret(false);
    onClose();
  };

  return (
    <CommonDialog
      open={open}
      onClose={handleClose}
      title="Razorpay Configuration"
      minWidth="600px"
    >
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500, color: "#0C2F49" }}
            >
              Razorpay Key ID <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="razorPay_key_id"
              type={showKeyId ? "text" : "password"}
              value={formData.razorPay_key_id}
              onChange={handleInputChange}
              placeholder="Enter Razorpay Key ID (e.g., rzp_test_R8ePElhVj5e2Bo)"
              size="small"
              disabled={saveLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowKeyId(!showKeyId)}
                      edge="end"
                      size="small"
                    >
                      {showKeyId ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500, color: "#0C2F49" }}
            >
              Razorpay Key Secret <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              type={showSecret ? "text" : "password"}
              name="razorPay_key_secret"
              value={formData.razorPay_key_secret}
              onChange={handleInputChange}
              placeholder="Enter Razorpay Key Secret"
              size="small"
              disabled={saveLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowSecret(!showSecret)}
                      edge="end"
                      size="small"
                    >
                      {showSecret ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              For security reasons, please verify before saving.
            </Typography>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleSwitchChange}
                  disabled={saveLoading}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Active
                </Typography>
              }
            />
          </Box>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={saveLoading}
          sx={{ minWidth: "100px" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSave}
          disabled={saveLoading}
          sx={{ minWidth: "100px" }}
        >
          {saveLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Update"
          )}
        </Button>
      </DialogActions>
    </CommonDialog>
  );
};

export default RazorPayConfigDialog;
